// Shared sort options for Collection and Cosmetics avatar grids.

import { normalizeCardId } from "./cardCatalog";
import { getCorgiTraits } from "./corgiTraits";

export const LS_COLLECTION_SORT_KEY = "mmu_collection_sort_v1";

export const COLLECTION_SORT_KEYS = Object.freeze({
    ID_ASC: "id_asc",
    ID_DESC: "id_desc",
    BACKGROUND: "background",
});

export const COLLECTION_SORT_OPTIONS = Object.freeze([
    { id: COLLECTION_SORT_KEYS.ID_ASC, label: "# (low → high)" },
    { id: COLLECTION_SORT_KEYS.ID_DESC, label: "# (high → low)" },
    { id: COLLECTION_SORT_KEYS.BACKGROUND, label: "Background" },
]);

const DEFAULT_SORT_KEY = COLLECTION_SORT_KEYS.ID_ASC;

const LEGACY_SORT_KEY_ALIASES = Object.freeze({
    background_asc: COLLECTION_SORT_KEYS.BACKGROUND,
    background_desc: COLLECTION_SORT_KEYS.BACKGROUND,
});

export function isValidCollectionSortKey(value) {
    return COLLECTION_SORT_OPTIONS.some((option) => option.id === value);
}

export function normalizeCollectionSortKey(value) {
    const aliased = LEGACY_SORT_KEY_ALIASES[value] || value;
    return isValidCollectionSortKey(aliased) ? aliased : DEFAULT_SORT_KEY;
}

export function loadCollectionSortKey() {
    try {
        const raw = window.localStorage.getItem(LS_COLLECTION_SORT_KEY);
        return normalizeCollectionSortKey(raw);
    } catch {
        return DEFAULT_SORT_KEY;
    }
}

export function saveCollectionSortKey(sortKey) {
    const normalized = normalizeCollectionSortKey(sortKey);
    try {
        window.localStorage.setItem(LS_COLLECTION_SORT_KEY, normalized);
    } catch {
        // ignore localStorage failures
    }
    return normalized;
}

export function getCardBackgroundSortKey(card = null) {
    if (!card) {
        return "";
    }

    const traits = getCorgiTraits(card.id);
    if (traits?.Backgrounds) {
        return String(traits.Backgrounds);
    }

    return "";
}

export function getCardBackgroundSortLabel(card = null) {
    const background = getCardBackgroundSortKey(card);
    if (background) {
        return background;
    }

    if (card?.name) {
        return String(card.name);
    }

    return "";
}

export function getAvatarEntryCardId(entry = null) {
    const cardId = entry?.item?.cardId;
    if (cardId !== null && cardId !== undefined && cardId !== "") {
        return normalizeCardId(cardId);
    }
    return null;
}

function compareNumbers(a, b) {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}

function compareStrings(a, b) {
    return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

function compareByIdAsc(cardA, cardB) {
    return compareNumbers(normalizeCardId(cardA.id), normalizeCardId(cardB.id));
}

function compareCardsBySortKey(cardA, cardB, sortKey) {
    if (sortKey === COLLECTION_SORT_KEYS.ID_DESC) {
        return -compareByIdAsc(cardA, cardB);
    }

    if (sortKey === COLLECTION_SORT_KEYS.BACKGROUND) {
        const bg = compareStrings(
            getCardBackgroundSortKey(cardA),
            getCardBackgroundSortKey(cardB)
        );
        return bg !== 0 ? bg : compareByIdAsc(cardA, cardB);
    }

    return compareByIdAsc(cardA, cardB);
}

export function sortCollectionCards(cards = [], sortKey = DEFAULT_SORT_KEY) {
    const normalizedKey = normalizeCollectionSortKey(sortKey);
    return [...(Array.isArray(cards) ? cards : [])].sort((a, b) =>
        compareCardsBySortKey(a, b, normalizedKey)
    );
}

function compareAvatarEntriesBySortKey(entryA, entryB, sortKey) {
    const cardA = { id: getAvatarEntryCardId(entryA), name: entryA?.item?.name };
    const cardB = { id: getAvatarEntryCardId(entryB), name: entryB?.item?.name };
    return compareCardsBySortKey(cardA, cardB, sortKey);
}

export function sortAvatarEntries(entries = [], sortKey = DEFAULT_SORT_KEY) {
    const normalizedKey = normalizeCollectionSortKey(sortKey);
    return [...(Array.isArray(entries) ? entries : [])].sort((a, b) =>
        compareAvatarEntriesBySortKey(a, b, normalizedKey)
    );
}

export function getCollectionSortOptionLabel(sortKey) {
    const option = COLLECTION_SORT_OPTIONS.find((item) => item.id === sortKey);
    return option?.label || COLLECTION_SORT_OPTIONS[0].label;
}
